import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { TbEyeglass2 } from 'react-icons/tb';
import AddShoppingCartRoundedIcon from '@mui/icons-material/AddShoppingCartRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import { useMenuPage } from '../hooks/useMenuPage';
import { useCoalescedRefresh } from '../hooks/useCoalescedRefresh';
import { useSiteSession } from '../context/SiteSessionContext';
import { getCollection, getCustomerLabel, getSiteLabel, getStockItemLabel, getUserLabel, requestJson, stripEmptyValues } from '../lib/api';
import { getSocket } from '../lib/socket';
import { playActionSound } from '../lib/sound';

const stockWorkflowOptions = {
  STOCK_LENS_STORE: {
    label: 'Télédétourage',
    description: 'Envoi de la forme, puis montage en magasin.',
  },
  STOCK_LENS_WORKSHOP: {
    label: 'Envoi monture Atelier (AMOI)',
    description: 'Monture envoyée à AMOI pour montage, puis retour magasin.',
  },
  STOCK_LENS_RAW_BL: {
    label: 'Envoi Palets Bruts',
    description: 'Picking atelier, mise directe en BL retour, puis réception du lot par le magasin.',
  },
};

function resolveStockProcess(form) {
  if (form.process === 'STOCK_LENS_RAW_BL') return 'STOCK_LENS_RAW_BL';
  return form.mountingLocation === 'workshop' ? 'STOCK_LENS_WORKSHOP' : 'STOCK_LENS_STORE';
}

function getSelectionIds(model) {
  if (Array.isArray(model)) return model;
  if (model?.ids instanceof Set) return [...model.ids];
  return [...(model?.ids || [])];
}

function getSelectedRows(rows, model) {
  const selectedIds = new Set(getSelectionIds(model).map(String));
  const isExcludeSelection = model?.type === 'exclude';

  return rows.filter((row) => (
    isExcludeSelection ? !selectedIds.has(String(row.id)) : selectedIds.has(String(row.id))
  ));
}

function getLensAssignmentKey(reservationId, index) {
  return `${reservationId}-${index}`;
}

function getValidationLensSlots(reservations) {
  return [...reservations]
    .sort((left, right) => {
      const leftDate = new Date(left.reservedAt || 0).getTime();
      const rightDate = new Date(right.reservedAt || 0).getTime();
      if (leftDate !== rightDate) return leftDate - rightDate;
      return String(left._id).localeCompare(String(right._id));
    })
    .flatMap((reservation) => Array.from(
      { length: Math.max(Number(reservation.quantity || 0), 0) },
      (_unused, index) => ({
        reservation,
        reservationId: String(reservation._id),
        index,
        key: getLensAssignmentKey(reservation._id, index),
      })
    ));
}

function formatDiopter(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '—';
  return `${numericValue >= 0 ? '+' : ''}${numericValue.toFixed(2).replace('.', ',')}`;
}

function getJobPatternError(reservation, jobNumber) {
  const pattern = String(reservation?.storeSiteId?.jobNumberPattern || '').trim();
  if (!pattern) return '';
  const normalizedJobNumber = String(jobNumber || '').trim();
  try {
    if (new RegExp(pattern, 'i').test(normalizedJobNumber)) return '';
  } catch (_error) {
    return 'Le pattern de numéro de job du magasin est invalide. Vérifie les paramètres du site.';
  }
  return `Numéro de job invalide : "${normalizedJobNumber || 'vide'}" ne correspond pas au format attendu pour ${reservation?.storeSiteId?.code || 'ce magasin'}.`;
}

function ChoiceTile({ selected, icon, label, onClick }) {
  return (
    <Button
      type="button"
      onClick={onClick}
      sx={{
        width: 156,
        height: 156,
        flexShrink: 0,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.85,
        px: 1.2,
        py: 1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'primary.main' : 'background.paper',
        color: selected ? 'primary.contrastText' : 'text.primary',
        boxShadow: selected ? '0 8px 18px rgba(25, 118, 210, 0.20)' : 'none',
        textAlign: 'center',
        textTransform: 'none',
        fontWeight: 900,
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: selected ? 'primary.dark' : 'action.hover',
        },
      }}
    >
      <Box sx={{ fontSize: 54, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
      <Typography variant="body2" sx={{ fontWeight: 950, lineHeight: 1.12 }}>
        {label}
      </Typography>
    </Button>
  );
}

export default function StockReservationsPage() {
  const menuPage = useMenuPage('/stock/reservations');
  const { isAdmin, site } = useSiteSession();
  const canManageReservations = isAdmin || site?.type === 'store';
  const [reservations, setReservations] = useState([]);
  const [reservationHistory, setReservationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationTarget, setValidationTarget] = useState(null);
  const [reservationSelectionModel, setReservationSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [validationForm, setValidationForm] = useState({
    jobNumber: '',
    frameType: 'new',
    mountingLocation: 'store',
    process: 'STOCK_LENS_STORE',
    frameDisplayLabel: '',
  });
  const [validationConfirmOpen, setValidationConfirmOpen] = useState(false);
  const [lensAssignments, setLensAssignments] = useState({});
  const [validating, setValidating] = useState(false);
  const [cancelReservationTarget, setCancelReservationTarget] = useState(null);
  const [editReservationTarget, setEditReservationTarget] = useState(null);
  const [editReservationForm, setEditReservationForm] = useState({ firstName: '', lastName: '' });
  const [savingReservation, setSavingReservation] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'info', message: '' });

  const validationProcess = resolveStockProcess(validationForm);

  const loadReservations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [payload, historyPayload] = await Promise.all([
        requestJson('/stock-reservations'),
        requestJson('/stock-reservations?history=true'),
      ]);
      setReservations(getCollection(payload));
      setReservationHistory(getCollection(historyPayload));
    } catch (err) {
      setError(err.message);
      setReservations([]);
      setReservationHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  const scheduleReservationsRefresh = useCoalescedRefresh(loadReservations, 350);

  useEffect(() => {
    const socket = getSocket();
    socket.on('stock-items:changed', scheduleReservationsRefresh);
    socket.on('commandes:changed', scheduleReservationsRefresh);

    return () => {
      socket.off('stock-items:changed', scheduleReservationsRefresh);
      socket.off('commandes:changed', scheduleReservationsRefresh);
    };
  }, [scheduleReservationsRefresh]);

  useEffect(() => {
    if (!error) return;
    setSnackbar({ open: true, severity: 'error', message: error });
  }, [error]);

  useEffect(() => {
    if (!snackbar.open || !snackbar.message) return;
    playActionSound(snackbar.severity);
  }, [snackbar.open, snackbar.message, snackbar.severity]);

  const reservationRows = useMemo(
    () =>
      reservations.map((reservation) => ({
        ...reservation,
        id: reservation._id,
        stockLabel: getStockItemLabel(reservation.stockItemId),
        storeLabel: [reservation.storeSiteId?.code, getSiteLabel(reservation.storeSiteId)].filter(Boolean).join(' - '),
        workshopLabel: [reservation.siteId?.code, getSiteLabel(reservation.siteId)].filter(Boolean).join(' - '),
        customerLabel: reservation.customerSnapshot?.fullName || getCustomerLabel(reservation.customerId),
        advisorLabel: reservation.advisorSnapshot?.fullName || getUserLabel(reservation.advisorId),
        reservedAtDisplay: reservation.reservedAt ? new Date(reservation.reservedAt).toLocaleString('fr-FR') : '',
      })),
    [reservations]
  );

  const selectedReservationRows = useMemo(
    () => getSelectedRows(reservationRows, reservationSelectionModel),
    [reservationRows, reservationSelectionModel]
  );
  const selectedReservationQuantity = selectedReservationRows.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
  const validationLensSlots = useMemo(
    () => getValidationLensSlots(Array.isArray(validationTarget) ? validationTarget : [validationTarget].filter(Boolean)),
    [validationTarget]
  );
  const selectedLensSides = validationLensSlots.map((slot) => lensAssignments[slot.key] || '');
  const hasValidLensAssignments =
    selectedLensSides.length > 0 &&
    selectedLensSides.every((side) => ['right', 'left'].includes(side)) &&
    (selectedLensSides.length === 1 || new Set(selectedLensSides).size === selectedLensSides.length);

  const historyReservationRows = useMemo(
    () =>
      reservationHistory.map((reservation) => ({
        ...reservation,
        id: reservation._id,
        stockLabel: getStockItemLabel(reservation.stockItemId),
        storeLabel: [reservation.storeSiteId?.code, getSiteLabel(reservation.storeSiteId)].filter(Boolean).join(' - '),
        workshopLabel: [reservation.siteId?.code, getSiteLabel(reservation.siteId)].filter(Boolean).join(' - '),
        customerLabel: reservation.customerSnapshot?.fullName || getCustomerLabel(reservation.customerId) || '—',
        advisorLabel: reservation.advisorSnapshot?.fullName || getUserLabel(reservation.advisorId) || '—',
        jobLabel: reservation.jobNumber || reservation.commandeId?.jobNumber || reservation.commandeId?.workshopSheetNumber || '—',
        statusLabel:
          reservation.status === 'consumed'
            ? 'Validée'
            : reservation.status === 'cancelled'
              ? 'Annulée'
              : 'Expirée',
        reservedAtDisplay: reservation.reservedAt ? new Date(reservation.reservedAt).toLocaleString('fr-FR') : '—',
        completedAtDisplay: reservation.updatedAt ? new Date(reservation.updatedAt).toLocaleString('fr-FR') : '—',
      })),
    [reservationHistory]
  );

  const handleOpenValidation = useCallback((reservationOrRows) => {
    const targets = Array.isArray(reservationOrRows) ? reservationOrRows : [reservationOrRows].filter(Boolean);
    if (!targets.length) return;
    if (targets.length > 2) {
      setSnackbar({ open: true, severity: 'warning', message: 'Sélectionne 1 ou 2 réservations maximum.' });
      return;
    }
    const totalQuantity = targets.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    if (totalQuantity > 2) {
      setSnackbar({ open: true, severity: 'warning', message: 'Le job peut contenir 2 verres maximum.' });
      return;
    }
    const lensSlots = getValidationLensSlots(targets);
    setValidationTarget(targets);
    setLensAssignments(Object.fromEntries(lensSlots.map((slot, index) => [slot.key, index === 0 ? 'right' : 'left'])));
    setValidationForm({
      jobNumber: '',
      frameType: 'existing',
      mountingLocation: 'store',
      process: 'STOCK_LENS_STORE',
      frameDisplayLabel: 'Télédétourage',
    });
    setValidationOpen(true);
    setError('');
  }, []);

  const handleValidationSubmit = (event) => {
    event.preventDefault();
    const targets = Array.isArray(validationTarget) ? validationTarget : [validationTarget].filter(Boolean);
    if (!targets.length) return;
    const patternError = getJobPatternError(targets[0], validationForm.jobNumber);
    if (patternError) {
      setError(patternError);
      return;
    }
    if (!hasValidLensAssignments) {
      setError('Choisis un verre droit et un verre gauche pour les deux verres réservés.');
      return;
    }
    setValidationConfirmOpen(true);
  };

  const handleConfirmValidation = async () => {
    const targets = Array.isArray(validationTarget) ? validationTarget : [validationTarget].filter(Boolean);
    if (!targets.length) return;

    try {
      setValidating(true);
      setError('');
      const patternError = getJobPatternError(targets[0], validationForm.jobNumber);
      if (patternError) {
        setError(patternError);
        setValidationConfirmOpen(false);
        return;
      }
      await requestJson('/commandes/from-stock-reservations', {
        method: 'POST',
        body: JSON.stringify(stripEmptyValues({
          ...validationForm,
          reservationIds: targets.map((item) => item._id),
          lensAssignments: validationLensSlots.map((slot) => ({
            reservationId: slot.reservationId,
            side: lensAssignments[slot.key],
          })),
        })),
      });
      setValidationConfirmOpen(false);
      setValidationOpen(false);
      setReservationSelectionModel({ type: 'include', ids: new Set() });
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Réservation validée et envoyée dans le suivi atelier.',
      });
      await loadReservations();
    } catch (err) {
      setError(err.message);
    } finally {
      setValidating(false);
    }
  };

  const handleCancelReservation = useCallback(async (id) => {
    try {
      setError('');
      await requestJson(`/stock-reservations/${id}`, { method: 'DELETE' });
      setCancelReservationTarget(null);
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Réservation annulée. Le stock est de nouveau disponible.',
      });
      await loadReservations();
    } catch (err) {
      setError(err.message);
    }
  }, [loadReservations]);

  const handleOpenReservationEdit = useCallback((reservation) => {
    const parts = String(reservation.customerLabel || '').trim().split(/\s+/).filter(Boolean);
    setEditReservationTarget(reservation);
    setEditReservationForm({
      firstName: parts[0] || '',
      lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
    });
  }, []);

  const handleSaveReservationEdit = useCallback(async (event) => {
    event.preventDefault();
    if (!editReservationTarget) return;

    const customerFullName = [editReservationForm.firstName, editReservationForm.lastName]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' ');
    if (!customerFullName) {
      setError('Le nom et le prénom du client sont requis.');
      return;
    }

    try {
      setSavingReservation(true);
      setError('');
      await requestJson(`/stock-reservations/${editReservationTarget._id}`, {
        method: 'PATCH',
        body: JSON.stringify({ customerFullName }),
      });
      setEditReservationTarget(null);
      setSnackbar({ open: true, severity: 'success', message: 'Informations client de la réservation mises à jour.' });
      await loadReservations();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingReservation(false);
    }
  }, [editReservationForm.firstName, editReservationForm.lastName, editReservationTarget, loadReservations]);

  const reservationColumns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: 'Action',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: canManageReservations ? 186 : 90,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {canManageReservations && (
              <Button size="small" variant="contained" onClick={() => handleOpenValidation(params.row)}>
                Valider
              </Button>
            )}
            {canManageReservations && (
              <IconButton size="small" color="primary" onClick={() => handleOpenReservationEdit(params.row)}>
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            )}
            {canManageReservations && (
              <IconButton size="small" color="error" onClick={() => setCancelReservationTarget(params.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ),
      },
      { field: 'stockLabel', headerName: 'Verre réservé', minWidth: 260, flex: 1.25 },
      { field: 'quantity', headerName: 'Qté', type: 'number', minWidth: 80, align: 'center', headerAlign: 'center' },
      { field: 'storeLabel', headerName: 'Magasin', minWidth: 190, flex: 0.9 },
      { field: 'workshopLabel', headerName: 'Atelier stock', minWidth: 160, flex: 0.8 },
      { field: 'customerLabel', headerName: 'Client', minWidth: 170, flex: 0.8 },
      { field: 'advisorLabel', headerName: 'Conseiller', minWidth: 170, flex: 0.8 },
      { field: 'reservedAtDisplay', headerName: 'Réservé le', minWidth: 160, flex: 0.75 },
    ],
    [canManageReservations, handleOpenReservationEdit, handleOpenValidation]
  );

  const historyColumns = useMemo(
    () => [
      {
        field: 'statusLabel',
        headerName: 'Statut',
        minWidth: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value}
            color={params.row.status === 'consumed' ? 'success' : params.row.status === 'cancelled' ? 'error' : 'default'}
            sx={{ fontWeight: 850 }}
          />
        ),
      },
      { field: 'jobLabel', headerName: 'JOB', minWidth: 140, flex: 0.65 },
      { field: 'stockLabel', headerName: 'Verre réservé', minWidth: 280, flex: 1.35 },
      { field: 'quantity', headerName: 'Qté', type: 'number', minWidth: 80, align: 'center', headerAlign: 'center' },
      { field: 'storeLabel', headerName: 'Magasin', minWidth: 180, flex: 0.85 },
      { field: 'workshopLabel', headerName: 'Atelier stock', minWidth: 160, flex: 0.75 },
      { field: 'customerLabel', headerName: 'Client', minWidth: 180, flex: 0.85 },
      { field: 'advisorLabel', headerName: 'Conseiller', minWidth: 180, flex: 0.85 },
      { field: 'reservedAtDisplay', headerName: 'Réservée le', minWidth: 170, flex: 0.8 },
      { field: 'completedAtDisplay', headerName: 'Finalisée le', minWidth: 170, flex: 0.8 },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 850 }}>
              {menuPage.sectionTitle}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 950 }}>
              {menuPage.pageTitle}
            </Typography>
          </Box>
          <Button variant="outlined" size="large" startIcon={<RefreshIcon />} onClick={loadReservations} sx={{ fontWeight: 850 }}>
            Rafraîchir
          </Button>
        </Stack>
      </Box>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Réservations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Verres bloqués en attente de numéro de job et de choix de process.
            </Typography>
          </Box>
          {canManageReservations && (
            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end" sx={{ rowGap: 1 }}>
              <Chip
                label={`${selectedReservationRows.length} sélectionnée(s) • ${selectedReservationQuantity} verre(s)`}
                color={selectedReservationRows.length ? 'primary' : 'default'}
                variant={selectedReservationRows.length ? 'filled' : 'outlined'}
                sx={{ fontWeight: 850 }}
              />
              <Button
                variant="contained"
                startIcon={<AddShoppingCartRoundedIcon />}
                onClick={() => handleOpenValidation(selectedReservationRows)}
                disabled={!selectedReservationRows.length || selectedReservationRows.length > 2 || selectedReservationQuantity <= 0 || selectedReservationQuantity > 2}
                sx={{ alignSelf: { sm: 'center' }, fontWeight: 900 }}
              >
                Valider sélection
              </Button>
            </Stack>
          )}
        </Stack>
        <Box sx={{ height: 560, width: '100%' }}>
          <DataGrid
            rows={reservationRows}
            columns={reservationColumns}
            loading={loading}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50]}
            checkboxSelection={canManageReservations}
            rowSelectionModel={reservationSelectionModel}
            onRowSelectionModelChange={(model) => {
              const selectedRows = getSelectedRows(reservationRows, model);
              if (selectedRows.length > 2) {
                setSnackbar({ open: true, severity: 'warning', message: 'Sélectionne 2 réservations maximum.' });
                return;
              }
              setReservationSelectionModel(model);
            }}
            disableRowSelectionOnClick
            sx={{
              border: 0,
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </Box>
      </Card>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
        <Stack spacing={0.4} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Historique des réservations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Réservations validées dans un JOB, annulées ou expirées.
          </Typography>
        </Stack>
        <Box sx={{ height: 460, width: '100%' }}>
          <DataGrid
            rows={historyReservationRows}
            columns={historyColumns}
            loading={loading}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            sx={{
              border: 0,
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </Box>
      </Card>

      <Dialog
        open={validationOpen}
        onClose={() => {
          if (validating) return;
          setValidationConfirmOpen(false);
          setValidationOpen(false);
        }}
        fullWidth
        maxWidth="md"
      >
        <Box component="form" onSubmit={handleValidationSubmit}>
          <DialogTitle>Valider la réservation</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                autoFocus
                label="Numéro de job"
                value={validationForm.jobNumber}
                onChange={(event) => setValidationForm((current) => ({ ...current, jobNumber: event.target.value }))}
                required
                fullWidth
              />
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Articles
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Le plus ancien verre réservé est proposé à droite par défaut.
                  </Typography>
                </Box>
                <Table size="small" aria-label="Articles réservés">
                  <TableHead>
                    <TableRow>
                      <TableCell>Libellé</TableCell>
                      <TableCell>Fournisseur</TableCell>
                      <TableCell align="right">Sphère</TableCell>
                      <TableCell align="right">Cylindre</TableCell>
                      <TableCell sx={{ width: 150 }}>Position</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationLensSlots.map((slot) => {
                      const stockItem = slot.reservation.stockItemId || {};
                      const selectedSide = lensAssignments[slot.key] || '';

                      return (
                        <TableRow key={slot.key}>
                          <TableCell sx={{ minWidth: 200, fontWeight: 700 }}>
                            {stockItem.label || getStockItemLabel(stockItem) || '—'}
                          </TableCell>
                          <TableCell>{stockItem.supplier || '—'}</TableCell>
                          <TableCell align="right">{formatDiopter(stockItem.sphere)}</TableCell>
                          <TableCell align="right">{formatDiopter(stockItem.cylinder)}</TableCell>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              value={selectedSide}
                              onChange={(event) => {
                                const nextSide = event.target.value;
                                setLensAssignments((current) => {
                                  const nextAssignments = { ...current, [slot.key]: nextSide };
                                  if (validationLensSlots.length === 2) {
                                    const otherSlot = validationLensSlots.find((candidate) => candidate.key !== slot.key);
                                    if (otherSlot) nextAssignments[otherSlot.key] = nextSide === 'right' ? 'left' : 'right';
                                  }
                                  return nextAssignments;
                                });
                              }}
                              fullWidth
                            >
                              <MenuItem value="right">Verre droit</MenuItem>
                              <MenuItem value="left">Verre gauche</MenuItem>
                            </TextField>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Parcours
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 1.5 }}>
                  <ChoiceTile
                    selected={validationProcess === 'STOCK_LENS_STORE'}
                    icon={<TbEyeglass2 size={60} strokeWidth={2.1} />}
                    label="Télédétourage"
                    onClick={() => setValidationForm((current) => ({
                      ...current,
                      frameType: 'existing',
                      mountingLocation: 'store',
                      process: 'STOCK_LENS_STORE',
                      frameDisplayLabel: 'Télédétourage',
                    }))}
                  />
                  <ChoiceTile
                    selected={validationProcess === 'STOCK_LENS_WORKSHOP'}
                    icon={<BuildCircleRoundedIcon fontSize="inherit" />}
                    label="Envoi monture Atelier (AMOI)"
                    onClick={() => setValidationForm((current) => ({
                      ...current,
                      frameType: 'existing',
                      mountingLocation: 'workshop',
                      process: 'STOCK_LENS_WORKSHOP',
                      frameDisplayLabel: 'Monture envoyée à AMOI',
                    }))}
                  />
                  <ChoiceTile
                    selected={validationProcess === 'STOCK_LENS_RAW_BL'}
                    icon={<Inventory2RoundedIcon fontSize="inherit" />}
                    label="Envoi Palets Bruts"
                    onClick={() => setValidationForm((current) => ({
                      ...current,
                      frameType: 'unknown',
                      mountingLocation: 'store',
                      process: 'STOCK_LENS_RAW_BL',
                      frameDisplayLabel: 'Envoi Palets Bruts',
                    }))}
                  />
                </Box>
              </Stack>
              <Alert severity="info">
                <Stack direction="row" spacing={1} alignItems="center">
                  <InfoOutlinedIcon fontSize="small" />
                  <span>
                    <strong>{stockWorkflowOptions[validationProcess].label}</strong> : {stockWorkflowOptions[validationProcess].description}
                  </span>
                </Stack>
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setValidationConfirmOpen(false);
                setValidationOpen(false);
              }}
              disabled={validating}
            >
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={validating}>
              {validating ? 'Validation…' : 'Valider'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(editReservationTarget)}
        onClose={() => {
          if (!savingReservation) setEditReservationTarget(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <Box component="form" onSubmit={handleSaveReservationEdit}>
          <DialogTitle>Modifier la réservation</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Modifie les informations client avant la validation du JOB.
              </Typography>
              <TextField
                autoFocus
                label="Nom client"
                value={editReservationForm.lastName}
                onChange={(event) => setEditReservationForm((current) => ({ ...current, lastName: event.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Prénom client"
                value={editReservationForm.firstName}
                onChange={(event) => setEditReservationForm((current) => ({ ...current, firstName: event.target.value }))}
                required
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setEditReservationTarget(null)} disabled={savingReservation}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={savingReservation}>Enregistrer</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={validationConfirmOpen}
        onClose={validating ? undefined : () => setValidationConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirmer l’envoi de la commande</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Alert severity="warning">
              Cette action va valider la réservation et envoyer directement la commande dans le suivi atelier.
            </Alert>
            <Box>
              <Typography variant="body2" color="text.secondary">JOB</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>{validationForm.jobNumber || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Réservation</Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {validationTarget
                  ? (Array.isArray(validationTarget) ? validationTarget : [validationTarget])
                      .map((item) => `${getStockItemLabel(item.stockItemId)} x${item.quantity || 1}`)
                      .join(' + ')
                  : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Workflow</Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {stockWorkflowOptions[validationProcess].label}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationConfirmOpen(false)} disabled={validating}>
            Annuler
          </Button>
          <Button variant="contained" onClick={handleConfirmValidation} disabled={validating}>
            {validating ? 'Envoi…' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={Boolean(cancelReservationTarget)}
        itemLabel={`la réservation ${cancelReservationTarget?.stockLabel || ''}`}
        description={`Annuler cette réservation et remettre ${
          cancelReservationTarget?.quantity || 1
        } verre(s) en stock disponible ?`}
        onClose={() => setCancelReservationTarget(null)}
        onConfirm={() => handleCancelReservation(cancelReservationTarget._id)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
